import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useGuest } from '../context/GuestContext';
import { toast } from 'sonner';
import {
  Zap, Crown, TrendingUp, Headphones, Heart, Star, Music, Flame,
  Rocket, Award, ChevronRight, Loader2, Lock, Info, Users
} from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { API } from '../config/api';

const BADGE_ICONS = {
  crown: Crown,
  zap: Zap,
  'trending-up': TrendingUp,
  headphones: Headphones,
  heart: Heart,
  star: Star,
  music: Music,
  flame: Flame,
};

// ─── Influence Score Badge (compact, for profile/comments) ────
export function InfluenceScoreBadge({ userId, className = '' }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!userId) return;
    fetch(`${API}/influence/score/${userId}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setData(d); })
      .catch(() => {});
  }, [userId]);

  if (!data || data.influence_score <= 0) return null;
  const badge = data.badge || {};
  const Icon = BADGE_ICONS[badge.icon] || Star;

  return (
    <span
      data-testid="influence-score-badge"
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${className}`}
      style={{ borderColor: `${badge.color}40`, backgroundColor: `${badge.color}15`, color: badge.color }}
      title={`Influence: ${data.influence_score} • ${badge.label}`}
    >
      <Icon className="w-3 h-3" />
      <span>{Math.round(data.influence_score)}</span>
    </span>
  );
}

// ─── Influence Profile Card (full, for profile page) ────
export function InfluenceProfileCard({ userId, getAuthHeaders }) {
  const { user, isAuthenticated } = useAuth();
  const isOwn = user?.user_id === userId;
  const [data, setData] = useState(null);
  const [badges, setBadges] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    const headers = getAuthHeaders ? getAuthHeaders() : {};
    Promise.all([
      isOwn
        ? fetch(`${API}/influence/me`, { headers }).then(r => r.ok ? r.json() : null)
        : fetch(`${API}/influence/score/${userId}`).then(r => r.ok ? r.json() : null),
      fetch(`${API}/influence/badges/${userId}`).then(r => r.ok ? r.json() : null),
    ]).then(([scoreData, badgeData]) => {
      if (scoreData) setData(scoreData);
      if (badgeData) setBadges(badgeData);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [userId, isOwn, getAuthHeaders]);

  if (loading) return <div className="animate-pulse h-24 rounded-2xl bg-muted/30" />;
  if (!data) return null;

  const badge = data.badge || {};
  const Icon = BADGE_ICONS[badge.icon] || Star;
  const score = data.influence_score || 0;
  const rank = data.rank || badge.label || 'Rookie';
  const rp = data.rank_progress;
  const pct = rp ? rp.progress_pct : 100;

  return (
    <div data-testid="influence-profile-card" className="rounded-2xl bg-card/60 border border-border p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${badge.color}20` }}>
            <Icon className="w-4 h-4" style={{ color: badge.color }} />
          </div>
          <div>
            <h3 className="font-bold text-sm text-foreground">Listener Influence</h3>
            <p className="text-[10px] text-muted-foreground">{rank}</p>
          </div>
        </div>
        <span className="text-2xl font-black tabular-nums" style={{ color: badge.color }} data-testid="influence-score-value">
          {Math.round(score)}
        </span>
      </div>

      {/* Progress to Next Rank */}
      <div className="space-y-1">
        <div className="relative h-2.5 rounded-full bg-muted/30 overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 rounded-full transition-all duration-1000"
            style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${badge.color}60, ${badge.color})` }}
            data-testid="influence-progress-bar"
          />
        </div>
        {rp ? (
          <div className="flex justify-between items-center text-[10px]">
            <span className="text-muted-foreground">{rank}</span>
            <span className="text-muted-foreground">
              <span className="text-foreground font-semibold">{rp.points_needed}</span> pts to {rp.next_rank}
            </span>
          </div>
        ) : (
          <p className="text-[10px] text-center" style={{ color: badge.color }}>Max Rank Achieved</p>
        )}
      </div>

      {/* Stats Row */}
      <div className="flex items-center gap-4 text-[11px] text-muted-foreground">
        {data.streak_days > 0 && (
          <span className="flex items-center gap-1">
            <Flame className="w-3 h-3 text-orange-400" /> {data.streak_days}d streak
            {data.streak_multiplier > 1 && <span className="text-orange-400/80">({data.streak_multiplier}x)</span>}
          </span>
        )}
        <span>{data.total_actions_30d || 0} actions (30d)</span>
      </div>

      {/* Boost Info (own profile only) */}
      {isOwn && data.boosts_remaining !== undefined && (
        <div className="flex items-center gap-2 pt-1 border-t border-border/30">
          <Rocket className="w-3.5 h-3.5 text-violet-400" />
          <span className="text-[11px] text-muted-foreground">
            <strong className="text-foreground">{data.boosts_remaining}</strong> / {data.boost_limit} boosts today
          </span>
          <span className="text-[10px] text-violet-400/70 ml-auto">{data.boost_power}x power</span>
        </div>
      )}

      {/* Fan Impact */}
      {isOwn && data.fan_impact && (
        <div className="grid grid-cols-3 gap-2 pt-1 border-t border-border/30">
          {[
            { label: 'Boosted', value: data.fan_impact.posts_boosted, icon: Rocket },
            { label: 'Artists', value: data.fan_impact.artists_supported, icon: Users },
            { label: 'Trending', value: data.fan_impact.trending_assists, icon: TrendingUp },
          ].map(s => (
            <div key={s.label} className="text-center py-1">
              <s.icon className="w-3.5 h-3.5 mx-auto mb-0.5 text-muted-foreground/50" />
              <p className="text-sm font-bold text-foreground">{s.value}</p>
              <p className="text-[9px] text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Badges */}
      {badges && badges.unlocked?.length > 0 && (
        <div className="pt-1 border-t border-border/30">
          <p className="text-[10px] font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">Badges</p>
          <div className="flex flex-wrap gap-1.5">
            {badges.unlocked.map(b => {
              const BIcon = BADGE_ICONS[b.icon] || Star;
              return (
                <span
                  key={b.key}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold border"
                  style={{ borderColor: `${b.color}30`, backgroundColor: `${b.color}10`, color: b.color }}
                  data-testid={`influence-badge-${b.key}`}
                >
                  <BIcon className="w-2.5 h-2.5" /> {b.label}
                </span>
              );
            })}
          </div>
        </div>
      )}
      {badges && badges.locked?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1">
          {badges.locked.slice(0, 3).map(b => (
            <span
              key={b.key}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-medium text-muted-foreground/40 bg-muted/10 border border-border/20"
              title={b.condition}
            >
              <Lock className="w-2.5 h-2.5" /> {b.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Boost Button (for posts/tracks) ────
export function BoostButton({ contentType, contentId, getAuthHeaders, size = 'sm' }) {
  const { isAuthenticated } = useAuth();
  const { requireAuth } = useGuest();
  const [status, setStatus] = useState({ loading: true, boosted: false, total: 0, power: 0, boosters: [] });
  const [boosting, setBoosting] = useState(false);

  useEffect(() => {
    const headers = getAuthHeaders ? getAuthHeaders() : {};
    fetch(`${API}/influence/boost-status/${contentType}/${contentId}`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) setStatus({ loading: false, boosted: d.user_boosted, total: d.total_boosts, power: d.total_power, boosters: d.top_boosters || [] });
        else setStatus(prev => ({ ...prev, loading: false }));
      })
      .catch(() => setStatus(prev => ({ ...prev, loading: false })));
  }, [contentType, contentId, getAuthHeaders]);

  const handleBoost = async () => {
    if (!requireAuth('boost content')) return;
    if (!isAuthenticated) return;
    if (status.boosted) { toast.info('Already boosted today'); return; }
    setBoosting(true);
    try {
      const res = await fetch(`${API}/influence/boost/${contentType}/${contentId}`, {
        method: 'POST', headers: getAuthHeaders(),
      });
      if (res.ok) {
        const d = await res.json();
        setStatus(prev => ({ ...prev, boosted: true, total: prev.total + 1, power: prev.power + d.boost_power }));
        toast.success(d.message || 'Boosted!');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Boost failed');
      }
    } catch { toast.error('Boost failed'); }
    setBoosting(false);
  };

  if (status.loading) return null;

  return (
    <div className="inline-flex items-center gap-1.5" data-testid={`boost-btn-${contentId}`}>
      <button
        onClick={handleBoost}
        disabled={boosting || status.boosted}
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all
          ${status.boosted
            ? 'bg-violet-600/20 text-violet-400 border border-violet-500/30'
            : 'bg-muted/20 text-muted-foreground hover:bg-violet-600/15 hover:text-violet-400 border border-transparent hover:border-violet-500/20'}`}
      >
        {boosting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Rocket className={`w-3 h-3 ${status.boosted ? 'text-violet-400' : ''}`} />}
        <span>{status.boosted ? 'Boosted' : 'Boost'}</span>
        {status.total > 0 && <span className="text-[10px] opacity-60">{status.total}</span>}
      </button>

      {/* Top boosters avatars */}
      {status.boosters.length > 0 && (
        <div className="flex -space-x-1.5">
          {status.boosters.slice(0, 3).map((b, i) => (
            <Link
              key={b.user_id}
              to={`/u/${b.handle || b.user_id}`}
              className="w-5 h-5 rounded-full bg-muted overflow-hidden ring-1 ring-background"
              title={`${b.name} (${b.boost_power}x)`}
            >
              {b.avatar ? <img src={b.avatar} alt="" className="w-full h-full object-cover" /> :
                <div className="w-full h-full flex items-center justify-center text-[7px] font-bold">{(b.name || '?')[0]}</div>}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Top Supporters Card (for creator profiles) ────
export function TopSupportersCard({ creatorId }) {
  const [supporters, setSupporters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!creatorId) return;
    fetch(`${API}/influence/top-supporters/${creatorId}?limit=5`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setSupporters(d.supporters || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [creatorId]);

  if (loading || supporters.length === 0) return null;

  return (
    <div data-testid="top-supporters-card" className="rounded-2xl bg-card/50 border border-border p-3 space-y-2">
      <div className="flex items-center gap-2">
        <Heart className="w-4 h-4 text-pink-400" />
        <h3 className="font-bold text-xs uppercase tracking-wider text-foreground/80">Top Supporters</h3>
      </div>
      <div className="space-y-2">
        {supporters.slice(0, 5).map((s, i) => {
          const BadgeIcon = s.badge ? (BADGE_ICONS[s.badge.icon] || Star) : null;
          return (
            <Link
              key={s.user_id}
              to={`/u/${s.handle || s.user_id}`}
              className="flex items-center gap-2 hover:bg-muted/20 rounded-lg p-1.5 transition-colors"
              data-testid={`supporter-${s.rank}`}
            >
              <span className={`w-5 text-center font-black text-xs ${i === 0 ? 'text-amber-400' : i === 1 ? 'text-zinc-400' : i === 2 ? 'text-amber-700' : 'text-muted-foreground/40'}`}>
                {s.rank}
              </span>
              <div className="w-7 h-7 rounded-full bg-muted overflow-hidden flex-shrink-0">
                {s.avatar ? <img src={s.avatar} alt="" className="w-full h-full object-cover" /> :
                  <div className="w-full h-full flex items-center justify-center text-[9px] font-bold">{(s.name || '?')[0]}</div>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold truncate">{s.name}</p>
                {s.badge && BadgeIcon && (
                  <span className="text-[9px] font-medium" style={{ color: s.badge.color }}>
                    {s.badge.label}
                  </span>
                )}
              </div>
              <span className="text-[10px] text-muted-foreground">{s.total_engagement} actions</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

// ─── Influence Leaderboard (page/modal) ────
export function InfluenceLeaderboard({ scope = 'global', creatorId, limit = 20 }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let url = `${API}/influence/leaderboard?scope=${scope}&limit=${limit}`;
    if (scope === 'creator' && creatorId) url += `&creator_id=${creatorId}`;
    fetch(url)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) setData(d.leaderboard || d.supporters || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [scope, creatorId, limit]);

  if (loading) return <div className="animate-pulse h-40 rounded-2xl bg-muted/30" />;

  return (
    <div data-testid="influence-leaderboard" className="space-y-2">
      <div className="flex items-center gap-2 mb-3">
        <Award className="w-4 h-4 text-amber-400" />
        <h3 className="font-bold text-sm">
          {scope === 'creator' ? 'Top Fans' : 'Top Listeners'}
        </h3>
      </div>
      {data.length === 0 ? (
        <p className="text-xs text-muted-foreground/50 text-center py-6">No data yet. Start engaging!</p>
      ) : (
        <div className="space-y-1">
          {data.map((entry, i) => {
            const badge = entry.badge || {};
            const BIcon = BADGE_ICONS[badge.icon] || Star;
            return (
              <Link
                key={entry.user_id}
                to={`/u/${entry.handle || entry.user_id}`}
                className="flex items-center gap-2.5 p-2 rounded-xl hover:bg-muted/20 transition-colors"
                data-testid={`leaderboard-entry-${entry.rank}`}
              >
                <span className={`w-6 text-center font-black text-sm ${i === 0 ? 'text-amber-400' : i === 1 ? 'text-zinc-400' : i === 2 ? 'text-amber-700' : 'text-muted-foreground/30'}`}>
                  {entry.rank}
                </span>
                <div className="w-8 h-8 rounded-full bg-muted overflow-hidden flex-shrink-0">
                  {entry.avatar ? <img src={entry.avatar} alt="" className="w-full h-full object-cover" /> :
                    <div className="w-full h-full flex items-center justify-center text-xs font-bold">{(entry.name || '?')[0]}</div>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{entry.name}</p>
                  {badge.label && (
                    <span className="text-[9px] font-medium" style={{ color: badge.color }}>{badge.label}</span>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold" style={{ color: badge.color || '#8B5CF6' }}>
                    {entry.influence_score ? Math.round(entry.influence_score) : entry.total_engagement || 0}
                  </p>
                  {entry.streak_days > 0 && (
                    <p className="text-[9px] text-muted-foreground flex items-center gap-0.5">
                      <Flame className="w-2.5 h-2.5 text-orange-400" /> {entry.streak_days}d
                    </p>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
